import { z } from "zod";
import { ComposeRecipeSlotSchema, ComposeRecipeOutputSchema } from "./tools.js";
import { EuAllergenSchema } from "./recipes.js";
/**
 * POST /v1/recipes/compose (ADR-022 R1). Grounds one Amy-composed recipe
 * structure — the request carries ONLY the LLM-authored structure (ingredient
 * IDs, quantities, localized steps/title; ComposeRecipeOutputSchema's
 * `.strict()` shape rejects any nutrition/allergen key by construction, see
 * tools.ts) plus the slot it targets. The server (services/planning's
 * groundRecipe, src/compose/groundRecipe.ts) resolves every ingredient
 * against the registry, computes nutrition deterministically, derives the
 * allergen map and dietary classes, and checks the result against the
 * calling user's hard constraints — the validator's stable reason-code
 * vocabulary (api-contracts.md) is the only failure vocabulary this endpoint
 * ever returns. A recipe that grounds successfully is persisted
 * (provenance='amy_generated') and immediately eligible for this user's
 * future plans (shared/catalog.ts's `status = 'active'` filter, no
 * provenance filter). A recipe that fails grounding is never persisted.
 */
export const ComposeRecipeRequestSchema = z
    .object({
    slot: ComposeRecipeSlotSchema,
    structure: ComposeRecipeOutputSchema,
})
    .strict();
export const ComposeRecipeNutritionSchema = z
    .object({
    kcal: z.number(),
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
})
    .strict();
/**
 * Disclosure vocabulary is deliberately 'contains' | 'may_contain' only
 * (narrower than recipes.ts's RecipeAllergenRelationSchema, which also
 * carries 'unknown' for the GET /v1/recipes/{id} worst-case-per-ingredient
 * projection). A grounded ingredient's 'unknown' relation still BLOCKS a
 * user who declared that allergen (ADR-012 fail-closed — see groundRecipe's
 * BLOCKING_ALLERGEN_RELATIONS check); when it doesn't block anyone it is
 * folded into 'may_contain' for this disclosure array rather than dropped,
 * so an uncertain sourcing signal is never presented as a clean recipe.
 */
export const ComposeRecipeAllergenEntrySchema = z
    .object({
    allergen: EuAllergenSchema,
    relation: z.enum(["contains", "may_contain"]),
})
    .strict();
export const ComposeRecipeValidResponseSchema = z
    .object({
    status: z.literal("valid"),
    recipeId: z.string(),
    computedNutritionPerServing: ComposeRecipeNutritionSchema,
    allergens: z.array(ComposeRecipeAllergenEntrySchema),
    dietaryClasses: z.array(z.string()),
})
    .strict();
/** Reuses the validator's stable, non-negotiable reason-code vocabulary (primitives.ts ValidationReasonCodeSchema) — never a bespoke code for this endpoint. */
export const ComposeRecipeFailureSchema = z
    .object({
    code: z.enum([
        "ALLERGEN_CONFLICT",
        "HARD_EXCLUSION",
        "MISSING_COMPOSITION",
        "NUTRITION_OUT_OF_RANGE",
        "TIME_LIMIT_EXCEEDED",
    ]),
    path: z.string().optional(),
    subjectId: z.string().optional(),
})
    .strict();
export const ComposeRecipeRejectedResponseSchema = z
    .object({
    status: z.literal("rejected"),
    failures: z.array(ComposeRecipeFailureSchema).min(1),
})
    .strict();
export const ComposeRecipeResponseSchema = z.discriminatedUnion("status", [
    ComposeRecipeValidResponseSchema,
    ComposeRecipeRejectedResponseSchema,
]);
//# sourceMappingURL=composeRecipe.js.map